import { useEffect, useState, useRef } from 'react';
import { X, Mic, Volume2, ShoppingCart, Package, Sparkles, MessageCircle, Search, ArrowRight, MicOff } from 'lucide-react';
import { useTutorial, TutorialStep } from '../contexts/TutorialContext';
import { useVoice } from '../contexts/voice';

interface StepContent {
  title: string;
  description: string;
  icon: React.ReactNode;
  position: 'center' | 'bottom-right' | 'top';
  showMicCutout?: boolean;
  hint?: string;
}

const STEP_CONTENT: Record<TutorialStep, StepContent> = {
  welcome: {
    title: 'Welkom bij de Voice Demo!',
    description: 'Ontdek hoe je met je stem kunt winkelen. We begeleiden je stap voor stap door het proces.',
    icon: <Sparkles className="w-8 h-8" />,
    position: 'center',
  },
  click_mic: {
    title: 'Start de spraakassistent',
    description: 'Klik op de rode microfoon-knop rechtsonder om te beginnen.',
    icon: <Mic className="w-8 h-8" />,
    position: 'bottom-right',
    showMicCutout: true,
    hint: 'Klik op de microfoon',
  },
  greeting: {
    title: 'Luister naar de begroeting',
    description: 'De assistent stelt zich voor. Je microfoon is automatisch gedempt zodat je rustig kunt luisteren.',
    icon: <Volume2 className="w-8 h-8 animate-pulse" />,
    position: 'center',
    hint: 'Even geduld...',
  },
  unmute_prompt: {
    title: 'Tijd om te praten!',
    description: 'Zorg dat je in een rustige ruimte bent. Unmute je microfoon (klik op het microfoon-icoontje) en zeg:',
    icon: <MicOff className="w-8 h-8" />,
    position: 'center',
    showMicCutout: true,
    hint: '"Zoek naar shirts"',
  },
  wait_search: {
    title: 'Zoeken...',
    description: 'De assistent zoekt nu naar shirts voor je.',
    icon: <Search className="w-8 h-8 animate-spin" />,
    position: 'top',
    hint: 'Even geduld...',
  },
  buy_prompt: {
    title: 'Kies een product',
    description: 'Je ziet nu de zoekresultaten! Kies een shirt dat je leuk vindt en zeg bijvoorbeeld:',
    icon: <ShoppingCart className="w-8 h-8" />,
    position: 'top',
    hint: '"Voeg de eerste toe aan mijn winkelwagen"',
  },
  wait_cart_add: {
    title: 'Toevoegen...',
    description: 'De assistent voegt het product toe aan je winkelwagen.',
    icon: <ShoppingCart className="w-8 h-8 animate-bounce" />,
    position: 'top',
    hint: 'Even geduld...',
  },
  show_cart_prompt: {
    title: 'Bekijk je winkelwagen',
    description: 'Het product is toegevoegd! Vraag nu om je winkelwagen te bekijken:',
    icon: <ShoppingCart className="w-8 h-8" />,
    position: 'center',
    hint: '"Laat mijn winkelwagen zien"',
  },
  wait_cart_view: {
    title: 'Navigeren...',
    description: 'De assistent opent je winkelwagen.',
    icon: <ShoppingCart className="w-8 h-8" />,
    position: 'center',
    hint: 'Even geduld...',
  },
  order_prompt: {
    title: 'Plaats je bestelling',
    description: 'Je bent bijna klaar! Zeg nu:',
    icon: <Package className="w-8 h-8" />,
    position: 'center',
    hint: '"Ik wil afrekenen"',
  },
  wait_checkout: {
    title: 'Naar afrekenen...',
    description: 'De assistent brengt je naar de checkout pagina.',
    icon: <Package className="w-8 h-8" />,
    position: 'center',
    hint: 'Even geduld...',
  },
  complete: {
    title: 'Gefeliciteerd! 🎉',
    description: 'Je hebt de tutorial succesvol afgerond. Je weet nu hoe je met je stem kunt winkelen bij Wehkamp!',
    icon: <Sparkles className="w-8 h-8" />,
    position: 'center',
  },
};

const AGENT_CAPABILITIES = [
  { icon: <Search className="w-5 h-5" />, text: 'Producten zoeken op naam of categorie' },
  { icon: <ShoppingCart className="w-5 h-5" />, text: 'Winkelwagen beheren' },
  { icon: <Package className="w-5 h-5" />, text: 'Bestellingen bekijken en tracken' },
  { icon: <MessageCircle className="w-5 h-5" />, text: 'Vragen over producten beantwoorden' },
];

export default function TutorialOverlay() {
  const { isActive, currentStep, quitTutorial, markComplete, goToStep } = useTutorial();
  const { state: voiceState, muted, toggleMute, connect, disconnect } = useVoice();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevVoiceState = useRef(voiceState);
  const hasHeardGreeting = useRef(false);

  // Handle step transitions with animation
  const transitionToStep = (step: TutorialStep) => {
    setIsTransitioning(true);
    setTimeout(() => {
      goToStep(step);
      setIsTransitioning(false);
    }, 200);
  };

  // Watch for voice connection to advance from click_mic step
  useEffect(() => {
    if (!isActive) return;

    if (currentStep === 'click_mic' && (voiceState === 'connecting' || voiceState === 'connected')) {
      transitionToStep('greeting');
    }
  }, [isActive, currentStep, voiceState]);

  // Reset greeting flag when entering greeting step
  useEffect(() => {
    if (currentStep === 'greeting') {
      hasHeardGreeting.current = false;
    }
  }, [currentStep]);

  // Auto-mute when entering greeting step
  useEffect(() => {
    if (!isActive || currentStep !== 'greeting') return;

    // Mute mic during greeting so user can listen
    if (!muted && voiceState !== 'disconnected') {
      toggleMute();
    }
  }, [isActive, currentStep, muted, voiceState, toggleMute]);

  // Track voice state transitions and detect when greeting audio finishes
  useEffect(() => {
    if (!isActive || currentStep !== 'greeting') {
      prevVoiceState.current = voiceState;
      return;
    }

    // Mark that we've heard the agent speak (state was 'listening')
    if (voiceState === 'listening') {
      hasHeardGreeting.current = true;
    }

    // Agent finished speaking: transition FROM 'listening' TO 'connected'
    // AND we must have actually heard the greeting (not just initial connect)
    if (prevVoiceState.current === 'listening' && voiceState === 'connected' && hasHeardGreeting.current) {
      // Small delay to ensure UI is ready
      const timer = setTimeout(() => {
        transitionToStep('unmute_prompt');
      }, 300);
      prevVoiceState.current = voiceState;
      return () => clearTimeout(timer);
    }

    prevVoiceState.current = voiceState;
  }, [isActive, currentStep, voiceState]);

  // Auto-disconnect voice when tutorial completes
  useEffect(() => {
    if (currentStep === 'complete' && voiceState !== 'disconnected') {
      disconnect();
    }
  }, [currentStep, voiceState, disconnect]);

  // Handle continue button
  const handleContinue = () => {
    if (currentStep === 'welcome') {
      transitionToStep('click_mic');
    } else if (currentStep === 'complete') {
      markComplete();
    }
  };

  // Handle mic button click during tutorial
  const handleMicClick = () => {
    if (currentStep === 'click_mic') {
      connect();
    }
  };

  if (!isActive) return null;

  const stepContent = STEP_CONTENT[currentStep];
  const showContinueButton = currentStep === 'welcome' || currentStep === 'complete';
  const isWaitingStep = ['greeting', 'wait_search', 'wait_cart_add', 'wait_cart_view', 'wait_checkout'].includes(currentStep);

  // Calculate card position
  const getCardPosition = () => {
    switch (stepContent.position) {
      case 'bottom-right':
        return 'bottom-32 right-6 md:right-8';
      case 'top':
        return 'top-24 left-1/2 -translate-x-1/2';
      default:
        return 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2';
    }
  };

  // Steps where voice controls should be accessible
  const showVoiceControls = ['greeting', 'unmute_prompt', 'wait_search', 'buy_prompt', 'wait_cart_add', 'show_cart_prompt', 'wait_cart_view', 'order_prompt', 'wait_checkout'].includes(currentStep);

  // Steps where user needs to see the content (products, cart, etc.)
  const needsVisibleContent = ['buy_prompt', 'wait_cart_add', 'show_cart_prompt', 'wait_cart_view', 'order_prompt', 'wait_checkout'].includes(currentStep);

  return (
    <>
      {/* Full-screen blocker with backdrop - lighter when user needs to see content */}
      <div
        className={`fixed inset-0 z-[100] transition-all duration-500 ${
          needsVisibleContent
            ? 'bg-black/30'
            : 'bg-black/70 backdrop-blur-sm'
        }`}
      />

      {/* Spotlight for voice controls - matches VoiceOverlay position */}
      {showVoiceControls && currentStep !== 'unmute_prompt' && (
        <div
          className="fixed z-[101] pointer-events-none rounded-[2rem] border-2 border-primary/40 bg-primary/10 animate-pulse"
          style={{
            bottom: '12px',
            right: '12px',
            width: '72px',
            height: '200px'
          }}
        />
      )}

      {/* Spotlight for unmute button
          Calculated: main mic (56px) + gap (12px) + expand (44px) + gap (12px) = 124px
          So mute button bottom edge = 24px + 124px = 148px */}
      {currentStep === 'unmute_prompt' && (
        <div
          className="fixed z-[101] pointer-events-none"
          style={{
            bottom: '148px',
            right: '24px',
            width: '44px',
            height: '44px'
          }}
        >
          {/* Glowing ring around unmute button */}
          <div className="absolute inset-[-14px] rounded-full bg-primary/50 animate-glow" />
          <div className="absolute inset-[-8px] rounded-full border-2 border-primary animate-pulse" />
          {/* Pulsing arrow pointing to button */}
          <div className="absolute -left-14 top-1/2 -translate-y-1/2 animate-bounce">
            <svg className="w-8 h-8 text-primary" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
            </svg>
          </div>
        </div>
      )}

      {/* Spotlight cutout for mic button only (initial click) - mic btn is 56px at bottom-6 right-6 */}
      {currentStep === 'click_mic' && (
        <div
          className="fixed z-[101] pointer-events-none flex items-center justify-center"
          style={{
            bottom: '24px',
            right: '24px',
            width: '56px',
            height: '56px'
          }}
        >
          {/* Glowing ring around mic */}
          <div className="absolute inset-[-14px] rounded-full bg-primary/50 animate-glow" />
          <div className="absolute inset-[-8px] rounded-full border-2 border-primary animate-pulse" />
          {/* Pulsing arrow pointing to button */}
          <div className="absolute -left-14 top-1/2 -translate-y-1/2 animate-bounce">
            <svg className="w-8 h-8 text-primary" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
            </svg>
          </div>
          {/* Clickable area */}
          <button
            onClick={handleMicClick}
            className="absolute inset-0 rounded-full cursor-pointer pointer-events-auto"
            aria-label="Start voice assistant"
          />
        </div>
      )}

      {/* Quit button - always visible */}
      <button
        onClick={() => {
          disconnect();
          quitTutorial();
        }}
        className="fixed top-4 right-4 z-[102] flex items-center gap-2 px-4 py-2.5 bg-card/95 backdrop-blur-md border border-primary/30 rounded-full text-sm font-medium text-foreground hover:bg-card hover:border-primary/50 transition-all shadow-xl"
      >
        <X className="w-4 h-4" />
        <span className="hidden sm:inline">Tutorial stoppen</span>
      </button>

      {/* Main content card */}
      <div
        className={`fixed z-[102] w-[90%] max-w-md transition-all duration-300 ${getCardPosition()} ${
          isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
        }`}
      >
        <div className="bg-card/95 backdrop-blur-md border border-primary/30 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header with icon */}
          <div className="relative px-6 pt-6 pb-4 text-center">
            {/* Decorative gradient */}
            <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent" />

            {/* Icon */}
            <div className="relative inline-flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-primary/20 text-primary">
              {stepContent.icon}
            </div>

            {/* Title */}
            <h2 className="relative text-xl font-bold text-foreground">
              {stepContent.title}
            </h2>
          </div>

          {/* Body */}
          <div className="px-6 pb-6">
            {/* Description */}
            <p className="text-center text-muted-foreground mb-4">
              {stepContent.description}
            </p>

            {/* Hint (speech suggestion) */}
            {stepContent.hint && !isWaitingStep && (
              <div className="flex items-center justify-center gap-2 px-4 py-3 bg-primary/10 border border-primary/20 rounded-xl mb-4">
                <MessageCircle className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-primary font-medium">{stepContent.hint}</span>
              </div>
            )}

            {/* Waiting indicator */}
            {isWaitingStep && (
              <div className="flex items-center justify-center gap-3 py-2">
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-sm text-muted-foreground">{stepContent.hint}</span>
              </div>
            )}

            {/* Capabilities list for complete step */}
            {currentStep === 'complete' && (
              <div className="mt-4 space-y-3 border-t border-primary/10 pt-4">
                <p className="text-sm font-medium text-foreground text-center">
                  Wat kan de assistent nog meer?
                </p>
                <div className="space-y-2">
                  {AGENT_CAPABILITIES.map((cap, i) => (
                    <div key={i} className="flex items-center gap-3 px-3 py-2 bg-muted/50 rounded-lg">
                      <span className="text-primary">{cap.icon}</span>
                      <span className="text-sm text-foreground">{cap.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Continue button */}
            {showContinueButton && (
              <button
                onClick={handleContinue}
                className="mt-4 w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-white font-medium rounded-xl transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {currentStep === 'complete' ? 'Tutorial afsluiten' : 'Start de tutorial'}
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Progress indicator */}
          <div className="px-6 pb-4">
            <div className="flex justify-center gap-1.5">
              {['welcome', 'click_mic', 'unmute_prompt', 'buy_prompt', 'show_cart_prompt', 'order_prompt', 'complete'].map((step) => {
                const stepIndex = ['welcome', 'click_mic', 'greeting', 'unmute_prompt', 'wait_search', 'buy_prompt', 'wait_cart_add', 'show_cart_prompt', 'wait_cart_view', 'order_prompt', 'wait_checkout', 'complete'].indexOf(currentStep);
                const thisStepIndex = ['welcome', 'click_mic', 'greeting', 'unmute_prompt', 'wait_search', 'buy_prompt', 'wait_cart_add', 'show_cart_prompt', 'wait_cart_view', 'order_prompt', 'wait_checkout', 'complete'].indexOf(step);
                const isPast = thisStepIndex < stepIndex;
                const isCurrent = step === currentStep ||
                  (step === 'click_mic' && currentStep === 'greeting') ||
                  (step === 'unmute_prompt' && currentStep === 'wait_search') ||
                  (step === 'buy_prompt' && currentStep === 'wait_cart_add') ||
                  (step === 'show_cart_prompt' && currentStep === 'wait_cart_view') ||
                  (step === 'order_prompt' && currentStep === 'wait_checkout');

                return (
                  <div
                    key={step}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      isCurrent
                        ? 'w-8 bg-primary'
                        : isPast
                        ? 'w-3 bg-primary/60'
                        : 'w-3 bg-muted'
                    }`}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Arrow pointing to mic button */}
        {stepContent.position === 'bottom-right' && stepContent.showMicCutout && (
          <div className="absolute -bottom-6 right-4 text-primary animate-bounce">
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M5 12l7 7 7-7" />
            </svg>
          </div>
        )}
      </div>
    </>
  );
}
